<script setup>
import axios from "axios";
import { ref, computed, watch, onMounted } from "vue";
import { useLayoutComponents } from "@/composables/layout.js";
import { useStorage } from "@/composables/use-storage";
import Toast from "@/components/toast.vue";
import InfiniteScroll from "@/components/infinite-scroll.vue";
import ProfileHeader from "@/components/profile/ProfileHeader.vue";
import { useDatasets } from "@/composables/useDatasets";
import { useListings } from "@/composables/useListings";
import { useRouter, useRoute } from "vue-router";
import Purchases from "@/pages/user/purchases.vue";
import SellerSales from "@/pages/user/seller-sales.vue";
import ApiCreditsWallet from "@/pages/user/ApiCreditsWallet.vue";
import SubmittedBids from "@/pages/user/SubmittedBids.vue";
import SubmittedOffers from "@/pages/user/SubmittedOffers.vue";
import RFPsTab from "@/pages/user/RFPsTab.vue";
import ChangeGroupForm from "@/pages/user/ChangeGroupForm.vue";
import SetCountryForm from "@/pages/user/setCountryForm.vue";
import { useToast } from "@/composables/use-toast";
import MenuListMobile from "@/components/data-studio/menuListMobile.vue";
import Sidebar from "@/components/sidebar/Sidebar.vue";
import RFPsRequests from "@/pages/user/RFPsRequests.vue";
import RFPsResponses from "@/pages/user/RFPsResponses.vue";
import ListProposals from "@/pages/user/proposal/ListProposals.vue";
import SaveProposal from "@/pages/user/proposal/SaveProposal.vue";
import EvalResults from "@/pages/user/eval/EvalResults.vue";
import AnnotateStudio from "@/pages/user/eval/AnnotateStudio.vue";
import { getSidebarConfig } from "@/services/sidebarConfig.js";
import EvalBuilderEntrypointView from "@/pages/eval/eval-builder/EvalBuilderEntrypointView.vue";
import EvalDatasetsView from "@/pages/eval/eval-datasets/EvalDatasetsView.vue";
import ImportEvalView from "@/pages/eval/eval-datasets/importEvalView.vue";

const { addToast } = useToast();
const router = useRouter();
const route = useRoute();

const requestCardVisible = ref(false);

// Composables
const { Card, Modal } = useLayoutComponents();

const { userInfo, hasPermission, saveUserInfo } = useStorage();

const {
  datasets,
  processedDatasets,
  hasMoreItems: hasMoreDatasets,
  fetchDatasetsPage,
  createDatasetFileObject,
  loadMore: loadMoreDatasets,
} = useDatasets();
const {
  processedListings,
  hasMoreItems: hasMoreListings,
  fetchListingsPage,
  loadMore: loadMoreListings,
} = useListings();

const isSeller = ref(false);
const showChangeGroupModal = ref(false);
const changeGroupFormData = ref({});

const editingItem = ref(null);

const selectedDatasetId = ref(null);
const formData = ref({});
const isListing = ref(false);
const screenSize = computed(() => window.innerWidth);

const isLoading = ref({
  datasets: false,
  listings: false,
  evals: false,
});

const isPageChanging = ref(false);

const permissions = {
  datasets: "datasets",
  listings: "listings",
  rfp: "rfp",
};

const creator = computed(() => {
  return {
    id: userInfo.value.id || "",
    name: userInfo.value.username || "",
    wallet:
      userInfo.value.seller && userInfo.value.seller.payout_address
        ? userInfo.value.seller.payout_address
        : "",
    profile_pic: userInfo.value.profile_pic || "",
  };
});

const items = computed(() => {
  const result = [
    { id: "datasets", label: "Datasets" },
    { id: "listings", label: "Listings" },
    { id: "rfp", label: "RFPs" },
    { id: "eval", label: "Evals" },
  ];

  if (hasPermission("listings")) {
    result.push({ id: "sales", label: "Sales" });
    result.push({ id: "offers", label: "Offers" });
  }

  if (hasPermission("buy")) {
    result.push({ id: "purchases", label: "Purchases" });
    result.push({ id: "bids", label: "Bids" });
  }


  return result;
});

const icons = {
  datasets: `<svg width="18" height="18" stroke-width="0.3" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M16.5 0.75H1.5C1.33424 0.75 1.17527 0.815848 1.05806 0.933058C0.940848 1.05027 0.875 1.20924 0.875 1.375V12C0.875 12.3315 1.0067 12.6495 1.24112 12.8839C1.47554 13.1183 1.79348 13.25 2.125 13.25H15.875C16.2065 13.25 16.5245 13.1183 16.7589 12.8839C16.9933 12.6495 17.125 12.3315 17.125 12V1.375C17.125 1.20924 17.0592 1.05027 16.9419 0.933058C16.8247 0.815848 16.6658 0.75 16.5 0.75ZM2.125 5.75H5.25V8.25H2.125V5.75ZM6.5 5.75H15.875V8.25H6.5V5.75ZM15.875 2V4.5H2.125V2H15.875ZM2.125 9.5H5.25V12H2.125V9.5ZM15.875 12H6.5V9.5H15.875V12Z" fill="currentColor"/>
  </svg>`,
  dataset_edit: `<svg width="16" height="16" stroke-width="1.5" class="eval-icon-no-fill" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path fill="none" stroke="currentColor" d="M6.32524 12.2563L1.00001 14L2.77481 8.76794M6.32524 12.2563L13.2651 5.21033C13.7357 4.74762 14 4.12028 14 3.46617C14 2.81207 13.7357 2.18473 13.2651 1.72202C12.7942 1.2597 12.1557 1 11.4899 1C10.8242 1 10.1856 1.2597 9.71469 1.72202L2.77481 8.76794M6.32524 12.2563L2.77481 8.76794M4.54961 10.5125L9.70458 5.21033M7.96155 3.50194L11.487 6.95734"  stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,
  listings: `<svg width="18" height="18" stroke-width="0.4" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M8.125 2.5H5C4.66848 2.5 4.35054 2.6317 4.11612 2.86612C3.8817 3.10054 3.75 3.41848 3.75 3.75V16.25C3.75 16.5815 3.8817 16.8995 4.11612 17.1339C4.35054 17.3683 4.66848 17.5 5 17.5H8.125C8.45652 17.5 8.77446 17.3683 9.00888 17.1339C9.2433 16.8995 9.375 16.5815 9.375 16.25V3.75C9.375 3.41848 9.2433 3.10054 9.00888 2.86612C8.77446 2.6317 8.45652 2.5 8.125 2.5ZM8.125 16.25H5V3.75H8.125V16.25ZM15 2.5H11.875C11.5435 2.5 11.2255 2.6317 10.9911 2.86612C10.7567 3.10054 10.625 3.41848 10.625 3.75V16.25C10.625 16.5815 10.7567 16.8995 10.9911 17.1339C11.2255 17.3683 11.5435 17.5 11.875 17.5H15C15.3315 17.5 15.6495 17.3683 15.8839 17.1339C16.1183 16.8995 16.25 16.5815 16.25 16.25V3.75C16.25 3.41848 16.1183 3.10054 15.8839 2.86612C15.6495 2.6317 15.3315 2.5 15 2.5ZM15 16.25H11.875V3.75H15V16.25Z" fill="currentColor"/>
  </svg>`,
  listing_edit: `<svg width="16" height="16" stroke-width="1.5" class="eval-icon-no-fill" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path fill="none" stroke="currentColor" d="M6.32524 12.2563L1.00001 14L2.77481 8.76794M6.32524 12.2563L13.2651 5.21033C13.7357 4.74762 14 4.12028 14 3.46617C14 2.81207 13.7357 2.18473 13.2651 1.72202C12.7942 1.2597 12.1557 1 11.4899 1C10.8242 1 10.1856 1.2597 9.71469 1.72202L2.77481 8.76794M6.32524 12.2563L2.77481 8.76794M4.54961 10.5125L9.70458 5.21033M7.96155 3.50194L11.487 6.95734"  stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,
  auctions: `<svg width="20" height="20" stroke-width="0.2" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M19.0125 9.117L17.7625 7.867C17.5505 7.65524 17.2695 7.52668 16.9706 7.50474C16.6718 7.4828 16.375 7.56894 16.1344 7.74747L12.2562 3.86934C12.4349 3.62878 12.5211 3.33206 12.4993 3.03322C12.4775 2.73438 12.3491 2.45332 12.1375 2.24122L10.8875 0.991217C10.7714 0.875114 10.6336 0.783014 10.4819 0.720178C10.3302 0.657342 10.1677 0.625 10.0035 0.625C9.83932 0.625 9.67676 0.657342 9.52508 0.720178C9.3734 0.783014 9.23559 0.875114 9.11952 0.991217L4.11952 5.99122C4.00341 6.1073 3.91131 6.24511 3.84848 6.39678C3.78564 6.54846 3.7533 6.71103 3.7533 6.8752C3.7533 7.03938 3.78564 7.20195 3.84848 7.35362C3.91131 7.5053 4.00341 7.64311 4.11952 7.75919L5.36952 9.00919C5.58157 9.22062 5.86248 9.34891 6.16114 9.3707C6.4598 9.3925 6.75636 9.30635 6.99686 9.12794L7.55702 9.68809L2.4492 14.7959C2.10986 15.167 1.92675 15.6548 1.93799 16.1575C1.94923 16.6603 2.15396 17.1393 2.50956 17.4949C2.86515 17.8505 3.3442 18.0553 3.84696 18.0665C4.34972 18.0777 4.83745 17.8946 5.20858 17.5553L10.3156 12.4475L10.8758 13.0068C10.6971 13.2474 10.6109 13.5441 10.6327 13.843C10.6545 14.1418 10.7829 14.4229 10.9945 14.635L12.2445 15.885C12.3606 16.0011 12.4984 16.0932 12.6501 16.156C12.8018 16.2188 12.9643 16.2512 13.1285 16.2512C13.2927 16.2512 13.4552 16.2188 13.6069 16.156C13.7586 16.0932 13.8964 16.0011 14.0125 15.885L19.0125 10.885C19.1286 10.7689 19.2207 10.6311 19.2835 10.4794C19.3464 10.3277 19.3787 10.1652 19.3787 10.001C19.3787 9.83681 19.3464 9.67424 19.2835 9.52256C19.2207 9.37089 19.1286 9.23308 19.0125 9.117ZM6.25311 8.12559L5.00311 6.87559L10.0031 1.87559L11.2531 3.12559L6.25311 8.12559ZM4.32499 16.6709C4.19325 16.8019 4.01504 16.8754 3.82928 16.8754C3.64353 16.8754 3.46532 16.8019 3.33358 16.6709C3.20286 16.5392 3.1295 16.3612 3.1295 16.1756C3.1295 15.99 3.20286 15.812 3.33358 15.6803L8.44061 10.5725L9.43202 11.5631L4.32499 16.6709ZM7.89374 8.25528L11.3828 4.76622L15.2375 8.62091L11.7492 12.11L7.89374 8.25528ZM13.1281 15.0006L11.8781 13.7506L12.1906 13.4381L16.5656 9.06309L16.8781 8.75059L18.1281 10.0006L13.1281 15.0006Z" fill="currentColor"/>
  </svg>`,
  bids: `<svg width="20" height="12" viewBox="0 0 20 14" stroke-width="0.3" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M14.375 3.99766V3.5625C14.375 1.60312 11.4195 0.125 7.5 0.125C3.58047 0.125 0.625 1.60312 0.625 3.5625V6.6875C0.625 8.31953 2.67578 9.61641 5.625 10.0047V10.4375C5.625 12.3969 8.58047 13.875 12.5 13.875C16.4195 13.875 19.375 12.3969 19.375 10.4375V7.3125C19.375 5.69531 17.3891 4.39687 14.375 3.99766ZM18.125 7.3125C18.125 8.34531 15.7195 9.5 12.5 9.5C12.2086 9.5 11.9195 9.48984 11.6344 9.47109C13.3195 8.85703 14.375 7.85938 14.375 6.6875V5.26094C16.7086 5.60859 18.125 6.55234 18.125 7.3125ZM5.625 8.73828V6.87969C6.2467 6.96069 6.87304 7.00089 7.5 7C8.12696 7.00089 8.7533 6.96069 9.375 6.87969V8.73828C8.75422 8.82999 8.12751 8.87569 7.5 8.875C6.87249 8.87569 6.24578 8.82999 5.625 8.73828ZM13.125 5.58828V6.6875C13.125 7.34297 12.1555 8.04687 10.625 8.47422V6.64844C11.6336 6.40391 12.4875 6.03984 13.125 5.58828ZM7.5 1.375C10.7195 1.375 13.125 2.52969 13.125 3.5625C13.125 4.59531 10.7195 5.75 7.5 5.75C4.28047 5.75 1.875 4.59531 1.875 3.5625C1.875 2.52969 4.28047 1.375 7.5 1.375ZM1.875 6.6875V5.58828C2.5125 6.03984 3.36641 6.40391 4.375 6.64844V8.47422C2.84453 8.04687 1.875 7.34297 1.875 6.6875ZM6.875 10.4375V10.1117C7.08047 10.1195 7.28828 10.125 7.5 10.125C7.80312 10.125 8.09922 10.1148 8.38984 10.0977C8.7127 10.2132 9.04157 10.3113 9.375 10.3914V12.2242C7.84453 11.7969 6.875 11.093 6.875 10.4375ZM10.625 12.4883V10.625C11.2465 10.7085 11.8729 10.7503 12.5 10.75C13.127 10.7509 13.7533 10.7107 14.375 10.6297V12.4883C13.1316 12.6706 11.8684 12.6706 10.625 12.4883ZM15.625 12.2242V10.3984C16.6336 10.1539 17.4875 9.78984 18.125 9.33828V10.4375C18.125 11.093 17.1555 11.7969 15.625 12.2242Z" fill="#8A8C91"/>
  </svg>`,
  offers: `<svg width="16" height="16" viewBox="0 0 16 18" stroke-width="0.3" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M13.6911 2.75084C13.3667 2.75038 13.0464 2.82273 12.7536 2.96255V2.43834C12.7538 1.91697 12.5678 1.41267 12.2291 1.01633C11.8904 0.619979 11.4212 0.357645 10.9062 0.276597C10.3912 0.19555 9.86412 0.301118 9.42004 0.574279C8.97596 0.84744 8.64404 1.27023 8.48411 1.76646C8.15098 1.58577 7.77663 1.49473 7.39774 1.50225C7.01884 1.50976 6.6484 1.61559 6.3227 1.80935C5.99701 2.00311 5.72725 2.27816 5.53984 2.60755C5.35243 2.93694 5.25381 3.30936 5.25364 3.68834V8.68834L4.9552 8.20943C4.66533 7.70919 4.1892 7.34405 3.6309 7.19382C3.07261 7.04359 2.47754 7.12049 1.97577 7.40772C1.47401 7.69494 1.10635 8.16913 0.953175 8.72663C0.799998 9.28412 0.873755 9.87959 1.15832 10.3829C2.40832 13.0211 3.41848 14.8922 4.57395 16.0618C5.74114 17.2461 7.06614 17.7508 9.00364 17.7508C10.8264 17.7488 12.5738 17.0238 13.8627 15.7349C15.1516 14.446 15.8766 12.6986 15.8786 10.8758V4.93834C15.8786 4.35817 15.6482 3.80178 15.2379 3.39154C14.8277 2.9813 14.2713 2.75084 13.6911 2.75084ZM14.6286 10.8758C14.627 12.3672 14.0338 13.797 12.9793 14.8515C11.9248 15.906 10.495 16.4992 9.00364 16.5008C7.41145 16.5008 6.38567 16.1196 5.46223 15.1844C4.42864 14.1368 3.47707 12.3602 2.27707 9.8274C2.27004 9.8119 2.26222 9.79678 2.25364 9.78209C2.12932 9.5667 2.09565 9.31075 2.16004 9.07055C2.22443 8.83034 2.38161 8.62555 2.597 8.50123C2.81238 8.37691 3.06833 8.34324 3.30854 8.40763C3.54875 8.47202 3.75353 8.6292 3.87785 8.84459C3.88104 8.85085 3.88469 8.85685 3.88879 8.86255L5.34739 11.2063C5.41904 11.3219 5.52645 11.411 5.65333 11.46C5.7802 11.5091 5.91961 11.5154 6.05039 11.478C6.18118 11.4406 6.29619 11.3616 6.37799 11.2529C6.45979 11.1442 6.5039 11.0119 6.50364 10.8758V3.68834C6.50364 3.43969 6.60241 3.20124 6.77822 3.02542C6.95404 2.84961 7.1925 2.75084 7.44114 2.75084C7.68978 2.75084 7.92823 2.84961 8.10405 3.02542C8.27986 3.20124 8.37864 3.43969 8.37864 3.68834V8.37584C8.37864 8.5416 8.44448 8.70057 8.5617 8.81778C8.67891 8.93499 8.83788 9.00084 9.00364 9.00084C9.1694 9.00084 9.32837 8.93499 9.44558 8.81778C9.56279 8.70057 9.62864 8.5416 9.62864 8.37584V2.43834C9.62864 2.18969 9.72741 1.95124 9.90322 1.77542C10.079 1.59961 10.3175 1.50084 10.5661 1.50084C10.8148 1.50084 11.0532 1.59961 11.229 1.77542C11.4049 1.95124 11.5036 2.18969 11.5036 2.43834V8.37584C11.5036 8.5416 11.5695 8.70057 11.6867 8.81778C11.8039 8.93499 11.9629 9.00084 12.1286 9.00084C12.2944 9.00084 12.4534 8.93499 12.5706 8.81778C12.6878 8.70057 12.7536 8.5416 12.7536 8.37584V4.93834C12.7536 4.68969 12.8524 4.45124 13.0282 4.27542C13.204 4.09961 13.4425 4.00084 13.6911 4.00084C13.9398 4.00084 14.1782 4.09961 14.354 4.27542C14.5299 4.45124 14.6286 4.68969 14.6286 4.93834V10.8758Z" fill="white"/>
  </svg>`,
  sales: `<svg width="12" height="18" stroke-width="0.5" viewBox="0 0 12 18" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M7.875 8.375H6.625V3.375H7.25C7.91304 3.375 8.54893 3.63839 9.01777 4.10723C9.48661 4.57607 9.75 5.21196 9.75 5.875C9.75 6.04076 9.81585 6.19973 9.93306 6.31694C10.0503 6.43415 10.2092 6.5 10.375 6.5C10.5408 6.5 10.6997 6.43415 10.8169 6.31694C10.9342 6.19973 11 6.04076 11 5.875C10.999 4.88076 10.6035 3.92753 9.90051 3.22449C9.19747 2.52145 8.24424 2.12603 7.25 2.125H6.625V0.875C6.625 0.70924 6.55915 0.550268 6.44194 0.433058C6.32473 0.315848 6.16576 0.25 6 0.25C5.83424 0.25 5.67527 0.315848 5.55806 0.433058C5.44085 0.550268 5.375 0.70924 5.375 0.875V2.125H4.75C3.75544 2.125 2.80161 2.52009 2.09835 3.22335C1.39509 3.92661 1 4.88044 1 5.875C1 6.86956 1.39509 7.82339 2.09835 8.52665C2.80161 9.22991 3.75544 9.625 4.75 9.625H5.375V14.625H4.125C3.46196 14.625 2.82607 14.3616 2.35723 13.8928C1.88839 13.4239 1.625 12.788 1.625 12.125C1.625 11.9592 1.55915 11.8003 1.44194 11.6831C1.32473 11.5658 1.16576 11.5 1 11.5C0.83424 11.5 0.675269 11.5658 0.558058 11.6831C0.440848 11.8003 0.375 11.9592 0.375 12.125C0.376034 13.1192 0.771454 14.0725 1.47449 14.7755C2.17753 15.4785 3.13076 15.874 4.125 15.875H5.375V17.125C5.375 17.2908 5.44085 17.4497 5.55806 17.5669C5.67527 17.6842 5.83424 17.75 6 17.75C6.16576 17.75 6.32473 17.6842 6.44194 17.5669C6.55915 17.4497 6.625 17.2908 6.625 17.125V15.875H7.875C8.86956 15.875 9.82339 15.4799 10.5267 14.7767C11.2299 14.0734 11.625 13.1196 11.625 12.125C11.625 11.1304 11.2299 10.1766 10.5267 9.47335C9.82339 8.77009 8.86956 8.375 7.875 8.375ZM4.75 8.375C4.08696 8.375 3.45107 8.11161 2.98223 7.64277C2.51339 7.17393 2.25 6.53804 2.25 5.875C2.25 5.21196 2.51339 4.57607 2.98223 4.10723C3.45107 3.63839 4.08696 3.375 4.75 3.375H5.375V8.375H4.75ZM7.875 14.625H6.625V9.625H7.875C8.53804 9.625 9.17393 9.88839 9.64277 10.3572C10.1116 10.8261 10.375 11.462 10.375 12.125C10.375 12.788 10.1116 13.4239 9.64277 13.8928C9.17393 14.3616 8.53804 14.625 7.875 14.625Z" fill="#9A9EA5"/>
  </svg>`,
  rfps: `<svg width="16" height="16" stroke-width="0.3" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M4.875 5.5C4.875 5.33424 4.94085 5.17527 5.05806 5.05806C5.17527 4.94085 5.33424 4.875 5.5 4.875H10.5C10.6658 4.875 10.8247 4.94085 10.9419 5.05806C11.0592 5.17527 11.125 5.33424 11.125 5.5C11.125 5.66576 11.0592 5.82473 10.9419 5.94194C10.8247 6.05915 10.6658 6.125 10.5 6.125H5.5C5.33424 6.125 5.17527 6.05915 5.05806 5.94194C4.94085 5.82473 4.875 5.66576 4.875 5.5ZM5.5 8.625H10.5C10.6658 8.625 10.8247 8.55915 10.9419 8.44194C11.0592 8.32473 11.125 8.16576 11.125 8C11.125 7.83424 11.0592 7.67527 10.9419 7.55806C10.8247 7.44085 10.6658 7.375 10.5 7.375H5.5C5.33424 7.375 5.17527 7.44085 5.05806 7.55806C4.94085 7.67527 4.875 7.83424 4.875 8C4.875 8.16576 4.94085 8.32473 5.05806 8.44194C5.17527 8.55915 5.33424 8.625 5.5 8.625ZM8 9.875H5.5C5.33424 9.875 5.17527 9.94085 5.05806 10.0581C4.94085 10.1753 4.875 10.3342 4.875 10.5C4.875 10.6658 4.94085 10.8247 5.05806 10.9419C5.17527 11.0592 5.33424 11.125 5.5 11.125H8C8.16576 11.125 8.32473 11.0592 8.44194 10.9419C8.55915 10.8247 8.625 10.6658 8.625 10.5C8.625 10.3342 8.55915 10.1753 8.44194 10.0581C8.32473 9.94085 8.16576 9.875 8 9.875ZM15.5 1.75V10.2414C15.5005 10.4056 15.4684 10.5683 15.4055 10.72C15.3426 10.8717 15.2502 11.0093 15.1336 11.125L11.125 15.1336C11.0093 15.2502 10.8717 15.3426 10.72 15.4055C10.5683 15.4684 10.4056 15.5005 10.2414 15.5H1.75C1.41848 15.5 1.10054 15.3683 0.866116 15.1339C0.631696 14.8995 0.5 14.5815 0.5 14.25V1.75C0.5 1.41848 0.631696 1.10054 0.866116 0.866116C1.10054 0.631696 1.41848 0.5 1.75 0.5H14.25C14.5815 0.5 14.8995 0.631696 15.1339 0.866116C15.3683 1.10054 15.5 1.41848 15.5 1.75ZM1.75 14.25H9.875V10.5C9.875 10.3342 9.94085 10.1753 10.0581 10.0581C10.1753 9.94085 10.3342 9.875 10.5 9.875H14.25V1.75H1.75V14.25ZM11.125 11.125V13.3672L13.3664 11.125H11.125Z" fill="#9A9EA5"/>
  </svg>`,
  rfp_edit: `<svg width="16" height="16" stroke-width="1.5" class="eval-icon-no-fill" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path fill="none" stroke="currentColor" d="M6.32524 12.2563L1.00001 14L2.77481 8.76794M6.32524 12.2563L13.2651 5.21033C13.7357 4.74762 14 4.12028 14 3.46617C14 2.81207 13.7357 2.18473 13.2651 1.72202C12.7942 1.2597 12.1557 1 11.4899 1C10.8242 1 10.1856 1.2597 9.71469 1.72202L2.77481 8.76794M6.32524 12.2563L2.77481 8.76794M4.54961 10.5125L9.70458 5.21033M7.96155 3.50194L11.487 6.95734"  stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,
  rfp_requests: `<svg width="18" height="15" stroke-width="0.3" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M17.75 8.37405C17.749 7.3798 17.3535 6.42658 16.6505 5.72354C15.9475 5.0205 14.9942 4.62508 14 4.62405H10.8906C10.6633 4.61077 6.70156 4.33186 2.92891 1.1678C2.74672 1.01479 2.52466 0.916956 2.2888 0.88578C2.05294 0.854604 1.81309 0.891383 1.59741 0.991799C1.38173 1.09221 1.19918 1.25209 1.07122 1.45266C0.943252 1.65322 0.875181 1.88614 0.875 2.12405V14.624C0.875032 14.862 0.942996 15.095 1.0709 15.2957C1.19881 15.4964 1.38135 15.6564 1.59705 15.7569C1.81276 15.8574 2.05268 15.8943 2.28861 15.8631C2.52454 15.832 2.74667 15.7341 2.92891 15.5811C5.87969 13.1061 8.94453 12.3967 10.25 12.1983V14.6764C10.2497 14.8824 10.3004 15.0852 10.3974 15.2669C10.4945 15.4486 10.6349 15.6035 10.8062 15.7178L11.6656 16.2905C11.8317 16.4013 12.022 16.4708 12.2204 16.493C12.4189 16.5152 12.6198 16.4896 12.8063 16.4182C12.9928 16.3468 13.1595 16.2318 13.2925 16.0828C13.4254 15.9337 13.5207 15.755 13.5703 15.5615L14.4898 12.0959C15.392 11.9759 16.2198 11.5324 16.8195 10.8478C17.4191 10.1632 17.7498 9.28413 17.75 8.37405ZM2.125 14.6186V2.12405C5.46953 4.92952 8.89297 5.63967 10.25 5.81467V10.9303C8.89453 11.1084 5.47188 11.817 2.125 14.6186ZM12.3594 15.2436V15.2522L11.5 14.6795V12.124H13.1875L12.3594 15.2436ZM14 10.874H11.5V5.87405H14C14.663 5.87405 15.2989 6.13744 15.7678 6.60628C16.2366 7.07512 16.5 7.71101 16.5 8.37405C16.5 9.03709 16.2366 9.67297 15.7678 10.1418C15.2989 10.6107 14.663 10.874 14 10.874Z" fill="#9A9EA5"/>
  </svg>`,
  rfp_responses: `<svg width="18" height="14" stroke-width="0.3" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M16.5 2H11.0836L8.91641 0.375C8.69972 0.213324 8.43676 0.125671 8.16641 0.125H4.625C4.29348 0.125 3.97554 0.256696 3.74112 0.491116C3.5067 0.725537 3.375 1.04348 3.375 1.375V2.625H2.125C1.79348 2.625 1.47554 2.7567 1.24112 2.99112C1.0067 3.22554 0.875 3.54348 0.875 3.875V12.625C0.875 12.9565 1.0067 13.2745 1.24112 13.5089C1.47554 13.7433 1.79348 13.875 2.125 13.875H14.0695C14.3825 13.8746 14.6825 13.7501 14.9038 13.5288C15.1251 13.3075 15.2496 13.0075 15.25 12.6945V11.375H16.5695C16.8825 11.3746 17.1825 11.2501 17.4038 11.0288C17.6251 10.8075 17.7496 10.5075 17.75 10.1945V3.25C17.75 2.91848 17.6183 2.60054 17.3839 2.36612C17.1495 2.1317 16.8315 2 16.5 2ZM14 12.625H2.125V3.875H5.66641L7.83359 5.5C8.05028 5.66168 8.31324 5.74933 8.58359 5.75H14V12.625ZM16.5 10.125H15.25V5.75C15.25 5.41848 15.1183 5.10054 14.8839 4.86612C14.6495 4.6317 14.3315 4.5 14 4.5H8.58359L6.41641 2.875C6.19972 2.71332 5.93676 2.62567 5.66641 2.625H4.625V1.375H8.16641L10.3336 3C10.5503 3.16168 10.8132 3.24933 11.0836 3.25H16.5V10.125Z" fill="#9A9EA5"/>
  </svg>`,
  purchases: `<svg width="18" height="16" stroke-width="0.5" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M17.75 9.625V14.625C17.75 14.9565 17.6183 15.2745 17.3839 15.5089C17.1495 15.7433 16.8315 15.875 16.5 15.875H1.5C1.16848 15.875 0.850537 15.7433 0.616116 15.5089C0.381696 15.2745 0.25 14.9565 0.25 14.625V9.625C0.25 9.29348 0.381696 8.97554 0.616116 8.74112C0.850537 8.5067 1.16848 8.375 1.5 8.375H4.625C4.79076 8.375 4.94973 8.44085 5.06694 8.55806C5.18415 8.67527 5.25 8.83424 5.25 9C5.25 9.16576 5.18415 9.32473 5.06694 9.44194C4.94973 9.55915 4.79076 9.625 4.625 9.625H1.5V14.625H16.5V9.625H13.375C13.2092 9.625 13.0503 9.55915 12.9331 9.44194C12.8158 9.32473 12.75 9.16576 12.75 9C12.75 8.83424 12.8158 8.67527 12.9331 8.55806C13.0503 8.44085 13.2092 8.375 13.375 8.375H16.5C16.8315 8.375 17.1495 8.5067 17.3839 8.74112C17.6183 8.97554 17.75 9.29348 17.75 9.625ZM8.55781 9.44219C8.61586 9.5003 8.68479 9.5464 8.76066 9.57785C8.83654 9.6093 8.91787 9.62549 9 9.62549C9.08213 9.62549 9.16346 9.6093 9.23934 9.57785C9.31521 9.5464 9.38414 9.5003 9.44219 9.44219L13.1922 5.69219C13.3095 5.57491 13.3753 5.41585 13.3753 5.25C13.3753 5.08415 13.3095 4.92509 13.1922 4.80781C13.0749 4.69054 12.9159 4.62465 12.75 4.62465C12.5841 4.62465 12.4251 4.69054 12.3078 4.80781L9.625 7.49141V0.875C9.625 0.70924 9.55915 0.550268 9.44194 0.433058C9.32473 0.315848 9.16576 0.25 9 0.25C8.83424 0.25 8.67527 0.315848 8.55806 0.433058C8.44085 0.550268 8.375 0.70924 8.375 0.875V7.49141L5.69219 4.80781C5.57491 4.69054 5.41585 4.62465 5.25 4.62465C5.08415 4.62465 4.92509 4.69054 4.80781 4.80781C4.69054 4.92509 4.62465 5.08415 4.62465 5.25C4.62465 5.41585 4.69054 5.57491 4.80781 5.69219L8.55781 9.44219ZM14.625 12.125C14.625 11.9396 14.57 11.7583 14.467 11.6042C14.364 11.45 14.2176 11.3298 14.0463 11.2589C13.875 11.1879 13.6865 11.1693 13.5046 11.2055C13.3227 11.2417 13.1557 11.331 13.0246 11.4621C12.8935 11.5932 12.8042 11.7602 12.768 11.9421C12.7318 12.124 12.7504 12.3125 12.8214 12.4838C12.8923 12.6551 13.0125 12.8015 13.1667 12.9045C13.3208 13.0075 13.5021 13.0625 13.6875 13.0625C13.9361 13.0625 14.1746 12.9637 14.3504 12.7879C14.5262 12.6121 14.625 12.3736 14.625 12.125Z" fill="#8A8C91"/>
  </svg>`,
  rfp_proposals: `<svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M10.3352 15.2205C10.3352 15.3679 10.2767 15.5091 10.1724 15.6133C10.0682 15.7175 9.92698 15.7761 9.7796 15.7761H4.22405C4.07671 15.7761 3.9354 15.7175 3.83121 15.6133C3.72703 15.5091 3.66849 15.3679 3.66849 15.2205C3.66849 15.0732 3.72703 14.9319 3.83121 14.8277C3.9354 14.7235 4.07671 14.665 4.22405 14.665H9.7796C9.92698 14.665 10.0682 14.7235 10.1724 14.8277C10.2767 14.9319 10.3352 15.0732 10.3352 15.2205ZM13.1129 6.33164C13.1153 7.25777 12.9061 8.17223 12.5012 9.00515C12.0964 9.83813 11.5065 10.5676 10.7768 11.1379C10.6404 11.2425 10.5296 11.3769 10.4531 11.5308C10.3766 11.6848 10.3362 11.8541 10.3352 12.026V12.4427C10.3352 12.7374 10.2181 13.0201 10.0097 13.2284C9.80138 13.4368 9.51873 13.5539 9.22405 13.5539H4.7796C4.48492 13.5539 4.20231 13.4368 3.99393 13.2284C3.78556 13.0201 3.66849 12.7374 3.66849 12.4427V12.026C3.66838 11.8562 3.62932 11.6886 3.55432 11.5363C3.47932 11.3838 3.37038 11.2507 3.23585 11.1469C2.50794 10.58 1.91853 9.85493 1.51223 9.02657C1.10593 8.19822 0.893399 7.28829 0.890716 6.36567C0.872661 3.05594 3.54766 0.299691 6.85461 0.220524C7.66943 0.200888 8.47995 0.34445 9.23847 0.642758C9.99694 0.94107 10.6881 1.38809 11.2713 1.95753C11.8544 2.52696 12.3178 3.20731 12.6341 3.9585C12.9504 4.7097 13.1132 5.51657 13.1129 6.33164ZM12.0018 6.33164C12.002 5.66473 11.8688 5.00453 11.61 4.38989C11.3512 3.77525 10.972 3.21859 10.4949 2.75269C10.0177 2.28678 9.45218 1.92103 8.83154 1.67699C8.21089 1.43294 7.5477 1.31551 6.881 1.33164C4.17266 1.39552 1.98725 3.65039 2.00183 6.35872C2.00437 7.11329 2.17849 7.85741 2.51103 8.53476C2.84357 9.21208 3.32582 9.80497 3.92127 10.2684C4.18894 10.4765 4.40545 10.7431 4.55423 11.0477C4.703 11.3524 4.78009 11.687 4.7796 12.026V12.4427H9.22405V12.026C9.22482 11.686 9.30324 11.3507 9.45331 11.0457C9.60339 10.7405 9.8212 10.4738 10.09 10.2657C10.6873 9.79884 11.1701 9.20177 11.5014 8.51999C11.8328 7.83818 12.0039 7.08968 12.0018 6.33164ZM10.8831 5.68303C10.739 4.87837 10.3519 4.13717 9.77382 3.5592C9.19572 2.98124 8.45443 2.59427 7.64974 2.45039C7.57779 2.43825 7.50415 2.44042 7.43303 2.45675C7.36191 2.47308 7.29471 2.50326 7.23526 2.54556C7.1758 2.58786 7.12526 2.64146 7.08652 2.70329C7.04778 2.76512 7.02159 2.83399 7.00947 2.90594C6.99734 2.9779 6.9995 3.05153 7.01583 3.12265C7.03215 3.19376 7.06233 3.26097 7.10463 3.32043C7.14694 3.37989 7.20054 3.43042 7.26238 3.46917C7.32421 3.50791 7.39307 3.53409 7.46503 3.54622C8.61572 3.73997 9.59211 4.71636 9.78725 5.86913C9.8092 5.99853 9.87631 6.11596 9.97658 6.20061C10.0768 6.28527 10.204 6.33168 10.3352 6.33164C10.3665 6.33144 10.3979 6.32889 10.4289 6.324C10.5741 6.2992 10.7035 6.21775 10.7888 6.09755C10.8739 5.97736 10.9079 5.82824 10.8831 5.68303Z" fill="white" stroke="white" stroke-width="0.266667"/>
  </svg>
  `,
  eval: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" stroke-width="0.3" viewBox="0 0 24 24"><g fill="currentColor"><path fill-rule="evenodd" d="M17.4 3c.3 0 .6.1.7.3l2.6 2.6c.4.3.4 1 0 1.4l-2.5 2.5l-4-4l2.5-2.5c.2-.2.5-.3.7-.3m-4.6 4.2l-9.5 9.5a1 1 0 0 0 0 1.4l2.6 2.6c.3.4 1 .4 1.4 0l9.5-9.5zM6 6c.6 0 1 .4 1 1v1h1a1 1 0 0 1 0 2H7v1a1 1 0 1 1-2 0v-1H4a1 1 0 0 1 0-2h1V7c0-.6.4-1 1-1m9 9c.6 0 1 .4 1 1v1h1a1 1 0 1 1 0 2h-1v1a1 1 0 1 1-2 0v-1h-1a1 1 0 1 1 0-2h1v-1c0-.6.4-1 1-1" clip-rule="evenodd"/><path d="M19 13h-2v2h2zM13 3h-2v2h2zm-2 2H9v2h2zM9 3H7v2h2zm12 8h-2v2h2zm0 4h-2v2h2z"/></g></svg>`,
  edit_evals: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="eval-icon-no-fill"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 8v8m0-8a2 2 0 1 0 0-4a2 2 0 0 0 0 4m0 8a2 2 0 1 0 0 4a2 2 0 0 0 0-4m8-8a2 2 0 1 0 0-4a2 2 0 0 0 0 4m0 0a4 4 0 0 1-4 4h-1a3 3 0 0 0-3 3"/></svg>`,
  edit_eval_listings: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" class="eval-icon-no-fill"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M6 14h2m3 0h5M3 7v10a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1"/></svg>`,
  eval_builder: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" stroke-width="0.3" viewBox="0 0 24 24"><g fill="currentColor"><path fill-rule="evenodd" d="M17.4 3c.3 0 .6.1.7.3l2.6 2.6c.4.3.4 1 0 1.4l-2.5 2.5l-4-4l2.5-2.5c.2-.2.5-.3.7-.3m-4.6 4.2l-9.5 9.5a1 1 0 0 0 0 1.4l2.6 2.6c.3.4 1 .4 1.4 0l9.5-9.5zM6 6c.6 0 1 .4 1 1v1h1a1 1 0 0 1 0 2H7v1a1 1 0 1 1-2 0v-1H4a1 1 0 0 1 0-2h1V7c0-.6.4-1 1-1m9 9c.6 0 1 .4 1 1v1h1a1 1 0 1 1 0 2h-1v1a1 1 0 1 1-2 0v-1h-1a1 1 0 1 1 0-2h1v-1c0-.6.4-1 1-1" clip-rule="evenodd"/><path d="M19 13h-2v2h2zM13 3h-2v2h2zm-2 2H9v2h2zM9 3H7v2h2zm12 8h-2v2h2zm0 4h-2v2h2z"/></g></svg>`,
  upload_datasets: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" class="eval-icon-no-fill"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 15v2a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-2M12 4v12m0-12l4 4m-4-4L8 8"/></svg>`
};

const sidebarItems = computed(() => {
  return getSidebarConfig(hasPermission, icons);
});

const initialPage = (item) => {
  const selectedPage = items.value.find((i) => i.id === item);
  if (selectedPage) {
    return selectedPage.id;
  }
  return items.value[0].id;
};

const activePage = ref(initialPage(route.query.page));
const showStorageCounter = computed(() => {
  return activePage.value === "datasets" && route.query.subpage === "edit-datasets";
});
const isAnnotateTaskView = computed(() => activePage.value === "eval-annotate" && route.query.taskId);
const showProfileAvatar = computed(() => !isAnnotateTaskView.value && activePage.value !== 'api-credits');
const showProfileName = computed(() => !isAnnotateTaskView.value && activePage.value !== 'api-credits');


const currentItems = computed(() => {
  if (activePage.value === "datasets") {
    return processedDatasets.value;
  } else if (activePage.value === "listings" || activePage.value === "evals") {
    return processedListings.value;
  }
  return [];
});

const loadMore = async () => {
  if (activePage.value === "listings" || activePage.value === "evals") {
    await loadMoreListings();
  } else if (activePage.value === "datasets") {
    await loadMoreDatasets();
  }
};

const fetchAllData = async () => {
  const loadingStates = {
    datasets: activePage.value === "datasets",
    listings: activePage.value === "listings",
    evals: activePage.value === "evals",
  };
  isLoading.value = { ...isLoading.value, ...loadingStates };

  try {
    if (activePage.value === "datasets") {
      await fetchDatasetsPage(1);
    } else if (activePage.value === "listings") {
      await fetchListingsPage(1, false);
    } else if (activePage.value === "evals") {
      await fetchListingsPage(1, true);
    }
  } catch (error) {
    console.error("Error fetching data:", error);
  } finally {
    isLoading.value = {
      ...isLoading.value,
      datasets: false,
      listings: false,
      evals: false,
    };
  }
};

const handleAddNew = async () => {
  const permissionKey =
    activePage.value === "evals" ? "datasets" : activePage.value;
  if (!hasPermission(permissions[permissionKey])) {
    addToast("You do not have permission to create this item.", "error");
    return;
  }

  editingItem.value = null;
  formData.value = {};
  isListing.value = activePage.value === "listings";

  if (activePage.value === "datasets") {
    router.push("/dataset-create");
  } else if (activePage.value === "listings") {
    router.push("/listing-create");
  } else if (activePage.value === "evals") {
    // Self-serve creators (datasets, no listings): private eval without marketplace listing
    if (hasPermission("listings")) {
      router.push({
        name: "listing-create",
        query: {
          subpage: "new-eval",
        },
      });
    } else {
      router.push({
        name: "creator-profile",
        query: {
          page: "eval-builder",
          subpage: "eval-builder",
        },
      });
    }
  }
};

const handleEditItem = async (item) => {
  isListing.value = activePage.value === "listings";

  if (activePage.value === "datasets") {
    if (item.eval_id) {
      router.push({
        name: "creator-profile",
        query: { 
          page: "eval-builder",
          subpage: "eval-builder",
          datasetId: item.dataset_id
        }
      });
    } else {
      router.push({
        name: "dataset-edit",
        params: { id: item.dataset_id },
      });
    }
  } else if (activePage.value === "listings" || activePage.value === "evals") {
    localStorage.setItem("temp_editing_listing_id", item.id);
    router.push({
      name: "listing-edit",
    });
  }
};

const handleChangeGroupFormData = (data) => {
  changeGroupFormData.value = data;
};

const handleChangeGroupSubmit = async () => {
  await validateChangeGroupForm(changeGroupFormData.value);
};

const validateChangeGroupForm = async (formData) => {
  try {
    if (formData.needsRFP === "yes" && !formData.rfpDescription.trim()) {
      addToast("Please fill in the description of the RFP", "error");
      return;
    }

    if (formData.wantToSell === "yes" && !formData.sellDescription.trim()) {
      addToast(
        "Please fill in the description of the datasets you want to sell",
        "error"
      );
      return;
    }

    if (formData.needsRFP === "yes" || formData.wantToSell === "yes") {
      const data = {
        user_id: userInfo.value.id,
        form: formData,
      };

      const response = await axios.post("api/change-group-requests", data);

      if (
        (response.data && response.status === 200) ||
        response.status === 201
      ) {
        addToast("Request sent successfully", "success");
        showChangeGroupModal.value = false;
      } else {
        addToast("Error sending request. Please try again.", "error");
      }
    } else {
      addToast("Please select at least one option", "error");
    }
  } catch (error) {
    console.error("Error sending request:", error);
  }
};

const verifyNeedInfos = () => {
  return hasPermission("datasets") && !userInfo.value.seller;
};
const needMoreInfo = ref(verifyNeedInfos());
const sellerFormData = ref({});

const handleSellerFormData = (data) => {
  sellerFormData.value = data;
};

const createSeller = async () => {
  requestStarted.value = true;
  if (!sellerFormData.value.country || !sellerFormData.value.postal_code) {
    addToast("Please fill in all fields", "error");
    requestStarted.value = false;
    return;
  }
  const response = await axios.post("api/sellers", {
    user_id: userInfo.value.id,
    country: sellerFormData.value.country,
    postal_code: sellerFormData.value.postal_code,
    status: "active",
  });
  if (response.status === 200 || response.status === 201) {
    saveUserInfo({
      ...userInfo.value,
      seller: {
        country: sellerFormData.value.country,
        postal_code: sellerFormData.value.postal_code,
      },
    });
    needMoreInfo.value = false;
    requestStarted.value = false;
    addToast("Your information has been saved", "success");
  } else {
    addToast("Error creating seller. Please try again.", "error");
  }
};
const requestStarted = ref(false);

const handleCloseSellerModal = () => {
  if (needMoreInfo.value && !requestStarted.value) {
    router.push("/explore");
  }
};

// Watchers
watch(
  selectedDatasetId,
  async (newValue, oldValue) => {
    if (newValue && activePage.value === "listings" && newValue !== oldValue) {
      const selectedDataset = datasets.value.find(
        (dataset) => dataset.id === newValue
      );

      if (selectedDataset) {
        const datasetFile = createDatasetFileObject(selectedDataset);
        formData.value = {
          ...formData.value,
          dataset_id: newValue,
          file_path: datasetFile,
        };
      } else {
        formData.value = {
          ...formData.value,
          dataset_id: null,
          file_path: null,
        };
      }
    }
  },
  { immediate: false }
);

watch(
  () => route.query.page,
  async (newPage) => {
    const pages = [
      "datasets",
      "listings",
      "evals",
      "eval-builder",
      "eval-datasets",
      "eval-import",
      "purchases",
      "api-credits",
      "bids",
      "offers",
      "sales",
      "rfp",
      "rfp-requests",
      "rfp-responses",
      "rfp-proposals-list",
      "new-proposal",
      "eval-results",
      "eval-annotate",
    ];
    if (newPage && pages.includes(newPage)) {
      isPageChanging.value = true;
      try {
        activePage.value = newPage;
      } finally {
        isPageChanging.value = false;
      }
    }
  },
  { immediate: true }
);

watch(activePage, async (newPage, oldPage) => {
  if (newPage !== oldPage) {
    isPageChanging.value = true;
    try {
      if (newPage === "listings") {
        await fetchListingsPage(1, false);
      } else if (newPage === "datasets") {
        await fetchDatasetsPage(1);
      } else if (newPage === "evals") {
        await fetchListingsPage(1, true);
      }
    } finally {
      isPageChanging.value = false;
    }
  }
});

onMounted(async () => {
  if (userInfo.value.seller) {
    isSeller.value = true;
  }
  await fetchAllData();

  setTimeout(() => {
    requestCardVisible.value = true;
  }, 500);
});

const handleMenuClick = async (item) => {
  isPageChanging.value = true;
  try {
    activePage.value = item.id;
  } finally {
    isPageChanging.value = false;
  }
};

const pagesDontEdit = [
  "purchases",
  "bids",
  "offers",
  "sales",
  "api-credits",
  "rfp",
  "rfp-requests",
  "rfp-responses",
  "rfp-proposals-list",
  "rfp-proposals-save",
  "new-proposal",
  "eval-results",
  "eval-builder",
  "eval-datasets",
  "eval-import",
  "eval-annotate",
];

const getRightTag = (listing) => {
  const statusToShow = {
    active: {
      text: 'Live',
      color: 'live',
    },
    inactive: {
      text: 'Draft',
      color: 'default',
    },
    draft: {
      text: 'Draft',
      color: 'default',
    },
  }
  if (listing.user_id) {
    return statusToShow.draft;
  }
  return statusToShow[listing.status];
}

const isSidebarCollapsed = ref(false);

const toggleSidebar = () => {

  isSidebarCollapsed.value = !isSidebarCollapsed.value;
};

const sidebarWidth = computed(() =>
  isSidebarCollapsed.value ? "w-16" : "w-64"
);

watch(
  () => route.query.subpage,
  (newSubpage) => {
    if (newSubpage && isSidebarCollapsed.value) {
      isSidebarCollapsed.value = false;
    }
  },
  { immediate: true }
);

const handleSidebarItemClick = (eventData) => {
  if (eventData.redirect) {
    router.push(eventData.redirect);
  }
};

const handleSidebarSubItemClick = (eventData) => {
  if (eventData.subItem?.id === 'new-dataset') {
    if (!hasPermission('datasets')) {
      addToast("You do not have permission to create a dataset.", "error");
      return;
    }
  } else if (eventData.subItem?.id === 'new-listing') {
    if (!hasPermission('listings')) {
      addToast("You do not have permission to create a listing.", "error");
      return;
    }
  }

  if (eventData.redirect) {
    const [path, query] = eventData.redirect.split('?');
    const queryParams = new URLSearchParams(query);

    router.push({
      path,
      query: {
        ...Object.fromEntries(queryParams),
        subpage: eventData.subItem?.id
      }
    });
  }
};

const getDatasetBadge = (dataset) => {
  let result = 'Upload';
  if (dataset.eval_id) {
    result = 'Eval';
  }

  if (dataset.external_source_id) {
    result = 'External';
  }
  return result;
}

</script>

<template>
  <Toast />
  <div v-if="!needMoreInfo" class="flex h-full w-full min-w-0 px-0">
    <div class="transition-all duration-300 hidden md:block flex-shrink-0" :class="sidebarWidth">
      <Sidebar :is-collapsed="isSidebarCollapsed" @toggle-collapse="toggleSidebar" :sections="sidebarItems"
        :toggle-position="'left'" :selected-item="activePage" :selected-sub-item="$route.query.subpage"
        :allow-multiple-expanded="false"
        @item-click="handleSidebarItemClick" @subitem-click="handleSidebarSubItemClick" />
    </div>

    <div class="flex-1 flex flex-col overflow-hidden min-w-0">
      <div :class="[
        'creator-profile__container flex-1 min-h-0',
        activePage === 'eval-annotate' ? 'flex flex-col overflow-hidden' : 'overflow-y-auto'
      ]">
        <div
          v-if="activePage !== 'eval-builder' && activePage !== 'eval-import'"
          class="creator-profile__hero flex-shrink-0"
          
        >
          <div class="flex flex-col">
            <div :class="isAnnotateTaskView || activePage === 'api-credits' ? '' : 'p-8'">
              <ProfileHeader
                ref="profileHeaderRef"
                :creator="creator"
                :showStorage="showStorageCounter"
                :showAvatar="showProfileAvatar"
                :showName="showProfileName"
              />
            </div>

            <div class="block md:hidden px-4 md:px-0">
              <MenuListMobile @handle-click="handleMenuClick" :active-tab="activePage" :tabs="items" />
            </div>
          </div>
        </div>

        <div v-if="isPageChanging" class="flex justify-center items-center h-full">
          <div class="loading-spinner"></div>
        </div>

        <template v-else-if="activePage === 'eval-builder' && hasPermission('datasets')">
          <div class="h-full">
            <EvalBuilderEntrypointView />
          </div>
        </template>

        <template v-else-if="activePage === 'eval-import' && hasPermission('datasets')">
          <div class="h-full">
            <ImportEvalView :selected-owner="userInfo.id" />
          </div>
        </template>

        <InfiniteScroll
          v-else
          :class="activePage === 'eval-annotate' ? 'flex-1 min-h-0 flex flex-col overflow-hidden' : ''"
          :load-more="loadMore"
          :loading="isLoading[activePage]"
          :has-more="(activePage === 'datasets') ? hasMoreDatasets : hasMoreListings"
          :has-items="currentItems.length > 0"
          :scroll-container="'.creator-profile__container'"
        >
          <div :class="[
            'w-full min-w-0 creator-profile__container',
            activePage === 'datasets' || activePage === 'listings' || activePage === 'evals'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6 p-4 md:pl-8 md:pb-8 md:pt-12 md:pr-4'
              : activePage === 'eval-annotate'
                ? 'p-4 flex-1 min-h-0 flex flex-col overflow-hidden'
                : 'p-4'
          ]">
            <Card v-if="!pagesDontEdit.includes(activePage) && screenSize >= 768" :is-add-card="true"
              :dataTestId="activePage === 'datasets' ? 'new-dataset-button' : 'new-listing-button'"
              @add="handleAddNew" />

            <template v-if="activePage === 'datasets'">
              <Card v-for="dataset in currentItems" :key="dataset.dataset_id" :title="dataset.dataset_title"
                :description="dataset.dataset_description" :deliveryMethod="getDatasetBadge(dataset)"
                :file="dataset.file" :schema="dataset.dataset_file_schema" :eval_id="dataset?.eval_id || null"
                :show-edit="screenSize >= 768" @edit="handleEditItem(dataset)" />
            </template>
            <template v-else-if="activePage === 'listings' || activePage === 'evals'">
              <Card
                v-for="listing in currentItems"
                :key="listing.id"
                :title="listing.name"
                :description="listing.description"
                :type="listing.type"
                :price="activePage === 'evals' ? listing.eval_price : listing.price"
                :deliveryMethod="listing.delivery_method"
                :image="listing.image"
                :tags="listing.tags"
                :eval_id="listing?.eval_id || listing?.datasets?.[0]?.dataset?.eval_id || null"
                :listingStatus="listing.status"
                :show-edit="screenSize >= 768"
                @edit="handleEditItem(listing)"
                :rightTagText="getRightTag(listing).text"
                :rightTagColor="getRightTag(listing).color"
                :is_open_source="listing.is_open_source === true"
              />
            </template>

            <template v-else-if="activePage === 'eval-datasets'">
              <EvalDatasetsView />
            </template>


            <template v-else-if="activePage === 'purchases'">
              <Purchases />
            </template>

            <template v-else-if="activePage === 'api-credits'">
              <ApiCreditsWallet />
            </template>

            <template v-else-if="activePage === 'sales'">
              <SellerSales />
            </template>

            <template v-else-if="activePage === 'bids'">
              <SubmittedBids />
            </template>

            <template v-else-if="activePage === 'offers'">
              <SubmittedOffers />
            </template>

            <template v-else-if="activePage === 'rfp'">
              <RFPsTab @showChangeGroupModal="showChangeGroupModal = true" />
            </template>

            <template v-else-if="activePage === 'rfp-requests'">
              <RFPsRequests />
            </template>

            <template v-else-if="activePage === 'rfp-responses'">
              <RFPsResponses />
            </template>

            <template v-else-if="activePage === 'rfp-proposals-list'">
              <ListProposals />
            </template>

            <template v-else-if="activePage === 'new-proposal'">
              <SaveProposal />
            </template>


            <template v-else-if="activePage === 'eval-results'">
              <EvalResults />
            </template>

            <template v-else-if="activePage === 'eval-annotate'">
              <div class="flex-1 min-h-0 flex flex-col">
                <AnnotateStudio />
              </div>
            </template>
          </div>

          <template #loading>
            <div class="loading-spinner"></div>
          </template>

          <template #end>
            <div class="text-gray-500 dark:text-gray-400">
              No more items to load
            </div>
          </template>
        </InfiniteScroll>
      </div>
    </div>
  </div>

  <Modal v-else :show="needMoreInfo" :title="'Before we proceed, we just need a little more information about you.'"
    :close-on-click-outside="false" :button-text="'Submit'" modal-size="medium" :button-loading-text="'Submitting...'"
    @close="handleCloseSellerModal" @save="createSeller">
    <SetCountryForm @change="handleSellerFormData" />
  </Modal>

  <Modal v-if="showChangeGroupModal" :show="showChangeGroupModal" :title="'Request access to more features?'"
    :close-on-click-outside="false" @close="() => (showChangeGroupModal = false)" :button-text="'Submit'"
    :button-loading-text="'Submitting...'" @save="handleChangeGroupSubmit">
    <ChangeGroupForm :key="'change-group-form'" @change="handleChangeGroupFormData" />
  </Modal>
</template>

<style scoped lang="postcss">
.creator-profile__hero {
  @apply relative;
  background-size: 100% 100%;
  background-position: 0px 0px;
  background-repeat: no-repeat;
}

.creator-profile__container {
  @apply overflow-y-auto w-full flex-1;
}

.creator-profile__header {
  @apply flex flex-col sm:flex-row gap-8 items-center;
  @apply text-center sm:text-left;
}

.creator-profile__avatar-container {
  @apply relative flex-shrink-0;
}

.creator-profile__avatar {
  @apply w-24 h-24 sm:w-32 sm:h-32 rounded-full object-cover ring-4 ring-echoes-primary/30;
}

.creator-profile__info {
  @apply flex-1;
}

.creator-profile__name-row {
  @apply w-full flex flex-col sm:flex-row items-center justify-between gap-4;
}

.creator-profile__name {
  @apply text-2xl sm:text-3xl font-bold text-white/80 tracking-tight;
}

.loading-spinner {
  @apply w-8 h-8 border-4 border-echoes-border-gray border-t-echoes-primary rounded-full animate-spin;
}
</style>
